import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { useShopPath } from '@/hooks/useShopPath';
import { deriveConnectionState } from '@/lib/connection-state';

function formatRelativeTime(iso) {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	if (!Number.isFinite(t)) return null;
	const diff = Date.now() - t;
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
	const days = Math.floor(hours / 24);
	return `${days} day${days === 1 ? '' : 's'} ago`;
}

// Only the no-recent-upload blocker fires today — Google disapprovals are
// flagged on the Google channel page (`ChannelSyncStatus`) where the
// per-reason breakdown lives, not here.
function blockerCopy(blocker, sourceLabel, needsReconnect) {
	const pos = sourceLabel || 'your POS';
	switch (blocker?.kind) {
		case 'no-recent-upload':
			return {
				pillLabel: 'Disconnected',
				message: needsReconnect
					? `We haven't received a stock update from ${pos} in ${blocker.days} days — reconnect it to bring your channels back up to date.`
					: `We haven't received a stock update from ${pos} in ${blocker.days} days — check it's uploading correctly.`
			};
		default:
			return null;
	}
}

// No POS source row at all — never connected, or the source was deleted.
// `hasEverIngested` picks the wording: a shop that once synced gets the
// reconnect framing, a brand-new one gets the connect framing.
function noSourceCopy(hasEverIngested) {
	return hasEverIngested
		? {
				heading: 'Your POS isn’t connected',
				pillLabel: 'Not connected',
				message: 'Stock changes from your till aren’t reaching any of your channels.',
				ctaLabel: 'Reconnect POS'
			}
		: {
				heading: 'No POS connected',
				pillLabel: 'Not connected',
				message: 'Connect your point-of-sale to start syncing your stock to your channels.',
				ctaLabel: 'Connect your POS'
			};
}

export function ConnectionStatusCard({ source, totalItems, inStockItems, hasEverIngested = false }) {
	const { shopPath } = useShopPath();
	const sourceLabel = source?.label ?? null;
	const lastSync = formatRelativeTime(source?.lastSyncAt);

	const noSource = !source;
	const state = deriveConnectionState({ source });
	const isWarning = noSource || state.status !== 'connected';
	const copy = noSource ? noSourceCopy(hasEverIngested) : blockerCopy(state.blocker, sourceLabel, state.needsReconnect);

	const iconWrap = isWarning ? 'bg-warning-soft' : 'bg-success-soft';
	const iconColor = isWarning ? 'text-warning-soft-foreground' : 'text-success-soft-foreground';
	const Icon = isWarning ? AlertTriangle : CheckCircle2;
	// Only the no-recent-upload blocker means the feed itself has stopped — the
	// other warning cases leave the connection up, so saying "Disconnected from X"
	// would be inaccurate (Keir's spot: 14-min-ago sync with file errors was
	// reading as Disconnected).
	const isFeedDown = state.blocker?.kind === 'no-recent-upload';
	const heading = noSource
		? copy.heading
		: isFeedDown
			? sourceLabel
				? `Disconnected from ${sourceLabel}`
				: 'Disconnected'
			: sourceLabel
				? `Connected to ${sourceLabel}`
				: 'Connected';
	const pillClass = isWarning ? 'bg-warning-soft text-warning-soft-foreground' : 'bg-success-soft text-success-soft-foreground';
	const pillLabel = copy?.pillLabel ?? 'Live';
	// Past the reconnect threshold (or with no source at all) the most useful
	// action is the POS connect flow, not the accuracy stats — the design's
	// card anatomy carries a single CTA, so it swaps rather than stacking
	// (NEA-5887). Below the threshold the existing accuracy link stays.
	const reconnectCta = noSource || state.needsReconnect;
	const ctaLabel = noSource ? copy.ctaLabel : state.needsReconnect ? 'Reconnect POS' : 'Inventory accuracy';
	const ctaTo = reconnectCta ? shopPath('/onboarding?step=3') : shopPath('/inventory/connection');
	const ctaClass = reconnectCta
		? 'bg-primary text-primary-foreground hover:bg-primary/90'
		: 'border border-border bg-white text-foreground hover:bg-accent';

	return (
		<SoftCard tone="white" className="!p-5 space-y-5 md:!p-6">
			<div className="flex items-start gap-4">
				<div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconWrap}`}>
					<Icon className={`h-5 w-5 ${iconColor}`} />
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="text-[15px] font-bold text-foreground md:text-base">{heading}</h3>
						<span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${pillClass}`}>
							{pillLabel}
						</span>
					</div>
					{copy?.message ? <p className="mt-1 text-sm text-foreground">{copy.message}</p> : null}
					{lastSync ? <div className="mt-1 text-xs tabular-nums text-muted-foreground">Last sync · {lastSync}</div> : null}
					{inStockItems != null && totalItems != null ? (
						<div className="mt-1 text-xs tabular-nums text-muted-foreground">
							<span className="font-semibold text-foreground">{inStockItems.toLocaleString()}</span> of{' '}
							<span className="tabular-nums">{totalItems.toLocaleString()}</span> items in stock
						</div>
					) : null}
				</div>
				<Link
					to={ctaTo}
					className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${ctaClass}`}
				>
					{ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
				</Link>
			</div>
		</SoftCard>
	);
}
